/**
 * Title/Escrow Company Management Page
 *
 * Main container that routes between dashboard and form views.
 */

import React, { useState } from 'react';
import { TitleEscrowDashboard } from './TitleEscrowDashboard';
import { TitleEscrowForm } from './TitleEscrowForm';
import { PermissionGate } from '@/components/PermissionGate';
import { ShieldX } from 'lucide-react';

type ViewState = 'dashboard' | 'create' | 'edit';

const AccessDeniedFallback: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center p-6">
      <ShieldX className="h-16 w-16 mx-auto text-gray-400 mb-4" />
      <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
      <p className="text-gray-600">You do not have permission to manage title and escrow companies.</p>
      <p className="text-sm text-gray-500 mt-2">Contact your administrator for access.</p>
    </div>
  </div>
);

export const TitleEscrowPage: React.FC = () => {
  const [view, setView] = useState<ViewState>('dashboard');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const handleCreateNew = () => {
    setSelectedCompanyId(null);
    setView('create');
  };

  const handleEdit = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setView('edit');
  };

  const handleBack = () => {
    setSelectedCompanyId(null);
    setView('dashboard');
  };

  const handleSaved = () => {
    setSelectedCompanyId(null);
    setView('dashboard');
  };

  return (
    <PermissionGate
      permissions={['manage_closing_costs']}
      fallback={<AccessDeniedFallback />}
    >
      {view === 'dashboard' && (
        <TitleEscrowDashboard
          onCreateNew={handleCreateNew}
          onEdit={handleEdit}
        />
      )}
      {(view === 'create' || view === 'edit') && (
        <TitleEscrowForm
          companyId={selectedCompanyId}
          onBack={handleBack}
          onSaved={handleSaved}
        />
      )}
    </PermissionGate>
  );
};

export default TitleEscrowPage;
